import React, {useEffect, useState} from 'react';
import {
    Button,
    Descriptions,
    Form,
    Input,
    message,
    Modal,
    Popconfirm,
    Select,
    Space,
    Table,
    Tag,
    Tooltip,
    Typography
} from 'antd';
import {DeleteOutlined, EyeOutlined} from '@ant-design/icons';
import {WaitlistItem, waitlistService} from '../../services/waitlistService';

const {Title, Text} = Typography;
const {TextArea} = Input;
const {Option} = Select;

const WaitlistManagement: React.FC = () => {
    const [list, setList] = useState<WaitlistItem[]>([]);
    const [loading, setLoading] = useState(false);
    const [detailModalVisible, setDetailModalVisible] = useState(false);
    const [selectedItem, setSelectedItem] = useState<WaitlistItem | null>(null);
    const [statusModalVisible, setStatusModalVisible] = useState(false);
    const [form] = Form.useForm();

    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 20,
        total: 0,
    });

    useEffect(() => {
        loadList();
    }, []);

    const loadList = async (page: number = 0, size: number = 20) => {
        setLoading(true);
        try {
            const data = await waitlistService.getList(page, size);
            setList(data.content);
            setPagination(prev => ({
                ...prev,
                current: data.number + 1,
                total: data.totalElements,
                pageSize: data.size,
            }));
        } catch (error) {
            message.error('获取等待列表失败');
        } finally {
            setLoading(false);
        }
    };

    const handleViewDetail = async (item: WaitlistItem) => {
        setSelectedItem(item);
        setDetailModalVisible(true);
    };

    const handleUpdateStatus = (item: WaitlistItem) => {
        setSelectedItem(item);
        form.setFieldsValue({status: item.status, remark: item.remark || ''});
        setStatusModalVisible(true);
    };

    const handleStatusSubmit = async () => {
        try {
            const values = await form.validateFields();
            await waitlistService.updateStatus(selectedItem!.id, values.status, values.remark);
            message.success('更新成功');
            setStatusModalVisible(false);
            loadList(pagination.current - 1);
        } catch (error: any) {
            message.error(error.message || '操作失败');
        }
    };

    const handleDelete = async (id: number) => {
        try {
            await waitlistService.delete(id);
            message.success('删除成功');
            loadList(pagination.current - 1);
        } catch (error) {
            message.error('删除失败');
        }
    };

    const getStatusTag = (status: string) => {
        switch (status) {
            case 'PENDING':
                return <Tag color="orange">待处理</Tag>;
            case 'REVIEWED':
                return <Tag color="blue">已查看</Tag>;
            case 'CONTACTED':
                return <Tag color="green">已联系</Tag>;
            default:
                return <Tag>{status}</Tag>;
        }
    };

    const formatTime = (value: string) => {
        if (!value) return '-';
        return value.replace('T', ' ').substring(0, 19);
    };

    const columns = [
        {
            title: 'ID',
            dataIndex: 'id',
            key: 'id',
            width: 70,
        },
        {
            title: '用户名称',
            dataIndex: 'name',
            key: 'name',
            width: 120,
        },
        {
            title: '邮箱',
            dataIndex: 'email',
            key: 'email',
            width: 200,
        },
        {
            title: '应用方向',
            dataIndex: 'direction',
            key: 'direction',
            width: 150,
        },
        {
            title: '状态',
            dataIndex: 'status',
            key: 'status',
            width: 100,
            render: (status: string) => getStatusTag(status),
        },
        {
            title: '创建时间',
            dataIndex: 'createdTime',
            key: 'createdTime',
            width: 180,
            render: (val: string) => formatTime(val),
        },
        {
            title: '操作',
            key: 'action',
            width: 160,
            render: (_: any, record: WaitlistItem) => (
                <Space size={4}>
                    <Tooltip title="详情">
                        <Button type="text" icon={<EyeOutlined/>} onClick={() => handleViewDetail(record)}
                                size="small"/>
                    </Tooltip>
                    <Tooltip title="更新状态">
                        <Button type="text" onClick={() => handleUpdateStatus(record)} size="small">
                            {getStatusTag(record.status)}
                        </Button>
                    </Tooltip>
                    <Tooltip title="删除">
                        <Popconfirm title="确定删除该记录吗？" onConfirm={() => handleDelete(record.id)}>
                            <Button type="text" danger icon={<DeleteOutlined/>} size="small"/>
                        </Popconfirm>
                    </Tooltip>
                </Space>
            ),
        },
    ];

    return (
        <div>
            <div style={{marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                <Title level={4} style={{margin: 0}}>等待列表</Title>
                <Text type="secondary">共 {pagination.total} 条记录</Text>
            </div>
            <Table
                columns={columns}
                dataSource={list}
                rowKey="id"
                loading={loading}
                pagination={{
                    current: pagination.current,
                    pageSize: pagination.pageSize,
                    total: pagination.total,
                    onChange: (page, size) => loadList(page - 1, size),
                    showSizeChanger: true,
                    showTotal: (total) => `共 ${total} 条`,
                }}
            />

            <Modal
                title="记录详情"
                open={detailModalVisible}
                onCancel={() => setDetailModalVisible(false)}
                footer={null}
                width={600}
            >
                {selectedItem && (
                    <Descriptions column={2} bordered size="small">
                        <Descriptions.Item label="ID">{selectedItem.id}</Descriptions.Item>
                        <Descriptions.Item label="用户名称">{selectedItem.name}</Descriptions.Item>
                        <Descriptions.Item label="邮箱" span={2}>{selectedItem.email}</Descriptions.Item>
                        <Descriptions.Item label="应用方向">{selectedItem.direction}</Descriptions.Item>
                        <Descriptions.Item label="状态">{getStatusTag(selectedItem.status)}</Descriptions.Item>
                        <Descriptions.Item label="备注" span={2}>{selectedItem.remark || '-'}</Descriptions.Item>
                        <Descriptions.Item label="IP地址">{selectedItem.ipAddress || '-'}</Descriptions.Item>
                        <Descriptions.Item label="创建时间">{formatTime(selectedItem.createdTime)}</Descriptions.Item>
                        <Descriptions.Item label="更新时间"
                                           span={2}>{formatTime(selectedItem.updatedTime)}</Descriptions.Item>
                    </Descriptions>
                )}
            </Modal>

            <Modal
                title="更新状态"
                open={statusModalVisible}
                onOk={handleStatusSubmit}
                onCancel={() => setStatusModalVisible(false)}
            >
                <Form form={form} layout="vertical">
                    <Form.Item name="status" label="状态" rules={[{required: true, message: '请选择状态'}]}>
                        <Select>
                            <Option value="PENDING">待处理</Option>
                            <Option value="REVIEWED">已查看</Option>
                            <Option value="CONTACTED">已联系</Option>
                        </Select>
                    </Form.Item>
                    <Form.Item name="remark" label="备注">
                        <TextArea rows={3}/>
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default WaitlistManagement;
